/**
 * 法人会員サービスLP (/corp) のコンテンツ定義。
 *
 * service.gyym.jp/10 (Studio.Design) の完全移植。文言・画像・リンク先は
 * 本番ページ (2026-09-17 時点) の PC 表示を正とする。
 * SP 幅のみ表示されていた古い文言 (施設数400 / 旧料金モデル等) は採用していない。
 */

const IMAGE_BASE = '/assets/images/corp/about'

export const lpImage = (file: string) => `${IMAGE_BASE}/${file}`

/** 資料請求フォームのアンカー (ページ内リンク) */
export const FORM_ANCHOR_ID = 'houjinform'

// 元ページの https://gyym.jp/... へのリンクは相対パスに置き換え
export const LINKS = {
  top: '/',
  form: `#${FORM_ANCHOR_ID}`,
  search: '/search',
  searchByDistance: '/facilities?sortBy=distance&order=asc',
  company: '/company',
  law: '/law',
  policy: 'https://service.gyym.jp/policy',
  term: 'https://service.gyym.jp/term',
  support: 'https://support.gyym.jp/hc/ja',
  partnerContact:
    'https://service.gyym.jp/facility2?utm_source=gyym.jp&utm_campaign=lp_men-ig',
  corpContact: '/corp',
} as const

export interface LpImage {
  src: string
  alt: string
}

/** ヒーロー下のロゴマーキー (元ページの並び順のまま。Kotoba Technologies は元ページでも2回登場) */
export const CLIENT_LOGOS: LpImage[] = [
  { src: lpImage('client-kotoba.webp'), alt: 'Kotoba Technologies' },
  { src: lpImage('client-nextbiz.webp'), alt: 'NEXTBIZ' },
  { src: lpImage('client-andas.webp'), alt: 'ANDas' },
  { src: lpImage('client-kotoba.webp'), alt: 'Kotoba Technologies' },
  { src: lpImage('client-bitrust.webp'), alt: 'BITRUST' },
  { src: lpImage('client-horton.webp'), alt: 'HORTON' },
  { src: lpImage('client-swoosh.webp'), alt: '' },
  { src: lpImage('client-avantia.webp'), alt: 'Avantia' },
  { src: lpImage('client-ryuka.webp'), alt: 'RYUKA' },
]

/** モーダル内の「写真 + ロゴ」行の右側 (ロゴ) */
export interface ModalLogo {
  src: string
  /** 表示サイズ (px)。指定がなければ 110px 四方 */
  width?: number
  height?: number
  /** SP での表示サイズ (px)。指定がなければ PC と同じ */
  spWidth?: number
  spHeight?: number
  /** ロゴ枠の背景色 */
  background: string
  /** ロゴ枠全体を背景画像として敷く (img ではなく bg cover) */
  cover?: boolean
}

/** アクティビティのタイルをクリックすると開く説明モーダル */
export interface ActivityModal {
  /** 欧文タイトル (Josefin Sans) */
  title: string
  /** 「【〇〇】とは」 */
  heading: string
  /** 説明文 (配列の要素ごとに改行) */
  body: string[]
  /** GYYMで利用できる施設の例 (エリア名) */
  locations: string
  photo: string
  /** 写真の background-position (指定がなければ中央) */
  photoPosition?: string
  logo: ModalLogo
}

export interface Activity {
  /** 表示ラベル (配列の要素ごとに改行) */
  label: string[]
  image: string
  modal: ActivityModal
}

const MEGALOS_LOGO: ModalLogo = {
  src: lpImage('brand-megalos.webp'),
  width: 110,
  height: 109,
  background: '#0B0C0D',
}

export const ACTIVITIES: Activity[] = [
  {
    label: ['マシンジム'],
    image: lpImage('activity-machine-gym.webp'),
    modal: {
      title: 'MACHINE GYM',
      heading: '【マシンジム】とは',
      body: [
        '筋肉のトレーニングやランニングをするための機械が利用できる施設のこと。',
        '各種マシンを効果的に使うことで、全身をバランスよくトレーングできる。',
        'その他にも、施設によっては、館内に入浴施設などのリラックスできる設備があったり、マシンを使ったトレーニング以外にもヨガや体幹ストレッチ、スイミングなどが利用できる。',
      ],
      locations:
        '日比谷/白金台/錦糸町/吉祥寺/田端/三鷹/武蔵小金井/立川/町田/調布/八王子/綱島/横浜天王町/相模大野/大和/市ヶ尾/上永谷/鷺沼/草加/本八幡/柏 etc',
      photo: lpImage('activity-machine-gym.webp'),
      logo: MEGALOS_LOGO,
    },
  },
  {
    label: ['ヨガ'],
    image: lpImage('activity-yoga.webp'),
    modal: {
      title: 'YOGA',
      heading: '【ヨガ】とは',
      body: [
        'ヨガとは心の安定を目的とし、呼吸・瞑想・ポーズを取り入れながら、心身共に健康で充実した生き方を目指していくためのものです。多くのストレスを抱えることの多い現代人の呼吸・姿勢・ココロを整え、心身ともに自分と向き合う時間を過ごしながら、理想のカラダへ近づけます。',
      ],
      locations: '千駄ヶ谷',
      photo: lpImage('activity-yoga.webp'),
      logo: { src: lpImage('modal-logo-yoga.webp'), background: '#FFFFFF' },
    },
  },
  {
    label: ['暗闇ボクシング'],
    image: lpImage('activity-dark-boxing.webp'),
    modal: {
      title: 'BOXING',
      heading: '【ボクシング】とは',
      body: [
        '拳にグローブを着用し、他のスポーツでは味わえない「殴る」「蹴る」という非日常的な行動でストレス発散を行うエクササイズ。',
        '全身で日常生活ではほとんど使わないような筋肉をしっかり鍛えることができ、女性の体の気になる部分をまんべんなく引き締めてくれる。',
        '全身を使い切っている満足感と、言葉にできないほどの爽快感は、他のトレーニングではなかなか味わうことはできないはず。',
      ],
      locations: '渋谷/赤坂',
      photo: lpImage('activity-dark-boxing.webp'),
      logo: {
        src: lpImage('modal-logo-boxing.webp'),
        width: 110,
        height: 42,
        background: '#000000',
      },
    },
  },
  {
    label: ['ホットヨガ'],
    image: lpImage('activity-hot-yoga.webp'),
    modal: {
      title: 'HOTYOGA',
      heading: '【ホットヨガ】とは',
      body: [
        '蒸し暑い部屋の中で行うヨガ。身体が温まり汗をかくことによって、リラクゼーション効果を発揮し、過敏になっていた神経が休まり、体内に溜まっていた老廃物と一緒にストレスが放出される。',
        'ダイエットやデトックス、ストレス発散、むくみ解消、美肌効果など様々な効果があり、',
        '女性からの人気が特に高い。',
      ],
      locations: '恵比寿/麻布十番/六本木/用賀/五反田',
      photo: lpImage('modal-photo-hot-yoga.webp'),
      logo: {
        src: lpImage('modal-logo-hot-yoga.webp'),
        width: 110,
        height: 41,
        background: '#FFFFFF',
      },
    },
  },
  {
    label: ['ボルダリング'],
    image: lpImage('activity-bouldering.webp'),
    modal: {
      title: 'BOULDERING',
      heading: '【ボルダリング】とは',
      body: [
        'ボルダリングとは、クライミングの一種で、最低限の道具で岩や人工の壁面などを登るスポーツのこと。腕の力だけで登るように思われがちですが、脚の力やお腹の力、背中の力と全身を使って登っていきます。シンプルで簡単そうに見えますが、“カラダ”だけでなく “アタマ”も使って登るためとても奥が深いスポーツです。',
      ],
      locations: '笹塚',
      photo: lpImage('modal-photo-bouldering.webp'),
      photoPosition: '50% 100%',
      logo: {
        src: lpImage('modal-logo-bouldering.webp'),
        width: 72,
        height: 71,
        background: '#EEEEEE',
      },
    },
  },
  {
    label: ['サーキット', 'トレーニング'],
    image: lpImage('activity-circuit-training.webp'),
    modal: {
      title: 'CIRCUIT TRAINING',
      heading: '【サーキットトレーニング】とは',
      body: [
        'サーキットトレーニングとは「無酸素運動」である筋トレと「有酸素運動」を交互に、繰り返しおこなうトレーニングのこと。筋トレと有酸素運動を同時におこない、心拍数が上がった状態で筋トレをすることでエネルギーの消費量が大きくアップします。高効率なトレーニングとしてダイエット目的の方にもおすすめです。',
      ],
      locations: '渋谷',
      photo: lpImage('activity-circuit-training.webp'),
      logo: {
        src: lpImage('modal-logo-circuit-training.webp'),
        background: '#0B0C0D',
      },
    },
  },
  {
    label: ['ピラティス'],
    image: lpImage('activity-pilates.webp'),
    modal: {
      title: 'PILATES',
      heading: '【ピラティス】とは',
      body: [
        'ピラティスとは体幹やインナーマッスルを鍛えることができるエクササイズ。',
        '硬くなっている筋肉をほぐすストレッチや、身体の中心にある「体幹」の筋肉を鍛える要素があるエクササイズなので、継続することで徐々に身体の歪みが調整され運動能力が向上してゆく。',
      ],
      locations: '新宿髙島屋',
      photo: lpImage('modal-photo-pilates.webp'),
      logo: {
        src: lpImage('modal-logo-pilates.webp'),
        width: 200,
        height: 60,
        spWidth: 140,
        spHeight: 42,
        background: '#FFFFFF',
      },
    },
  },
  {
    label: ['スイミング'],
    image: lpImage('activity-swimming.webp'),
    modal: {
      title: 'SWIMMING',
      heading: '【スイミング】とは',
      body: [
        '水泳技術の向上はもちろん、体力向上、健康維持など目的に合わせて、関節に負担をかけず行うことができるフィットネス。',
        '水泳は水の抵抗を受け、全身の筋肉を使うため他のフィットネスに比べて消費カロリーが激しく、競技としてだけではなく、ダイエットとして水泳に取り組んでいる方も多い。',
      ],
      locations:
        '葛飾/本八幡/横浜天王町/綱島/上永谷/市ヶ尾/鷺沼/相模大野/大和/三鷹/武蔵小金井/八王子/調布/町田/立川/草加/柏/吉祥寺 etc',
      photo: lpImage('activity-swimming.webp'),
      logo: MEGALOS_LOGO,
    },
  },
  {
    label: ['サイクリング'],
    image: lpImage('activity-cycling.webp'),
    modal: {
      title: 'CYCLING',
      heading: '【サイクリング】とは',
      body: [
        '自転車型のフィットネスマシンで、有酸素運動に向いている。脂肪燃焼効果のほか、代謝量の向上、血行促進効果など、美容にも健康にも良い効果が期待できる。',
        'VR映像を見ながら行うプログラムなどもあり、楽しみながら自分の限界にチャレンジできる。',
      ],
      locations: '表参道',
      photo: lpImage('modal-photo-cycling.webp'),
      logo: {
        src: lpImage('modal-logo-cycling.webp'),
        background: 'transparent',
        cover: true,
      },
    },
  },
]

export interface Point {
  number: number
  title: string
  body: string
}

export const POINTS: Point[] = [
  {
    number: 1,
    title: '幅広いエリアで利用可能！',
    body: '提携施設は950施設以上、約2,000プログラム。主要エリア（関東、関西、東海、九州、中四国、東北、北海道）に提携施設が拡がっていますので、支店勤務の従業員様や出張が多い従業員様にも広くご利用頂けます。',
  },
  {
    number: 2,
    title: '様々なブランド、フィットネスアクティビティから選べる！',
    body: 'ティップネス、メガロス、NAS、エニタイムフィットネス、スポーツオアシス、コ・ス・パ、EXPAなどの人気施設と提携しているGYYM。たくさんの中から選べるから、会社近くのジムと、従業員様の自宅近くのジムのブランドが異なっていても問題ありません。マシンジム、ホットヨガ、ピラティス、暗闇フィットネスと色々なアクティビティを利用できることから、男性、女性を問わず、従業員様のニーズに合うフィットネスがきっと見つかります。',
  },
  {
    number: 3,
    title: 'お得さが運動増進の秘訣！',
    body: 'ポイント還元で、通常よりお得に利用できます。毎月自動付与されるポイントで、従業員様は自己負担なく全国の提携施設を利用できるため、継続して利用したくなる仕組みです。',
  },
  {
    number: 4,
    title: '従業員様の運動状況が見える！',
    body: '福利厚生ご担当者様は、専用の管理画面を通じて従業員様のGYYMご利用状況を確認いただけます。ご担当者様のご負担なく、健康増進施策の効果や従業員様の運動ニーズが把握できます。',
  },
]

/** 提携ブランドロゴをクリックすると開く紹介モーダル */
export interface BrandModal {
  /** 欧文タイトル (Josefin Sans) */
  title: string
  /** キャッチコピー (配列の要素ごとに改行) */
  subtitle: string[]
  /** 紹介文 (配列の要素ごとに改行) */
  body: string[]
  photo: string
  logo: ModalLogo
}

export interface BrandLogo extends LpImage {
  modal: BrandModal
}

/** ポイント2 に並ぶ提携ブランドロゴ */
export const BRAND_LOGOS: BrandLogo[] = [
  {
    src: lpImage('brand-tipness.webp'),
    alt: 'TIPNESS',
    modal: {
      title: 'TIPNESS',
      subtitle: [
        'As a comprehensive fitness gym,',
        'we can provide you with a gym that fits your lifestyle!',
      ],
      body: [
        'For those who are too busy with work to make time for training, or those who have stopped training because they didn\'t feel any results, this is the perfect training environment to make your "I wish I could do it" or "I\'ll try again" wish come true. Why not try starting a new habit with the casualness of going to a convenience store?',
      ],
      photo: lpImage('modal-photo-tipness.webp'),
      logo: { src: lpImage('brand-tipness.webp'), background: '#000000' },
    },
  },
  {
    src: lpImage('brand-megalos.webp'),
    alt: 'MEGALOS',
    modal: {
      title: 'MEGALOS',
      subtitle: ['フィットネスを上質に、もっと快適に'],
      body: [
        '知識豊富なスタッフが、あなたの運動をサポート！',
        '充実のマシンと設備・サービスで、今の自分が求めている「自由」がここにはあります。',
      ],
      photo: lpImage('activity-machine-gym.webp'),
      logo: { ...MEGALOS_LOGO, background: '#000000' },
    },
  },
  {
    src: lpImage('brand-renaissance.webp'),
    alt: 'RENAISSANCE',
    modal: {
      title: 'RENAISSANCE',
      subtitle: [
        'マシンジム・プール・サウナ・ヨガと幅広いラインナップの多機能ジム',
      ],
      body: [
        '一人ひとり生き方が異なる現代人に寄り添い、その人らしいウェルビーイング、すなわち「誰もが、いつでも、よりよいココロとカラダでいつづけられる」方法を届けます。',
      ],
      photo: lpImage('modal-photo-renaissance.webp'),
      logo: {
        src: lpImage('brand-renaissance.webp'),
        width: 131,
        height: 131,
        background: '#FFFFFF',
      },
    },
  },
  {
    src: lpImage('brand-anytime-fitness.webp'),
    alt: 'ANYTIME FITNESS',
    modal: {
      title: 'ANYTIME FITNESS',
      subtitle: ['店舗数世界No.1ジムがあなたの生活にフィット'],
      body: [
        '平日は仕事場の近くの店舗で、休日は家の近くの店舗で。また出張先でも旅行先でも、トレーニング可能です。しっかり鍛えたいアスリートの方にもご満足いただけるマシンを揃えています。',
      ],
      photo: lpImage('modal-photo-anytime-fitness.webp'),
      logo: {
        src: lpImage('modal-logo-anytime-fitness.webp'),
        width: 140,
        height: 51,
        background: '#FFFFFF',
      },
    },
  },
  {
    src: lpImage('brand-expa.webp'),
    alt: 'EXPA',
    modal: {
      title: 'EXPA',
      subtitle: ['楽しいから成果が出る！RIZAPメソッド筋力トレーニング'],
      body: [
        '「カラダを引き締めたい。女性らしいカラダになりたい」と思っても、挫折してしまった経験はありませんか？そこでEXPAでは、「楽しみながら」筋肉をつけてなりたいBODYになることに徹底的にこだわり、RIZAPメソッドを取り入れた筋力トレーニングを、アップテンポな音楽と、暗闇と光が織りなす非日常的な空間で、集中して取り組める環境を完備しました。',
      ],
      photo: lpImage('modal-photo-expa.webp'),
      logo: { src: lpImage('brand-expa.webp'), background: '#000000' },
    },
  },
  {
    src: lpImage('brand-surf-fit.webp'),
    alt: 'Surf Fit JAPAN',
    modal: {
      title: 'Surf Fit',
      subtitle: [
        '都会の真ん中でサーフトリップ！日本初の「サーフエクササイズ」',
      ],
      body: [
        'Surf Fitはサーフボードの下にバランスボールを置いた専用機器を用意して行なう新感覚エクササイズです。西海岸をイメージさせる店内と、夏のビーチの気温に合わせた32度のスタジオで、不安定なボードの上で最新のヒットチューンにノリながら、楽しく体幹を鍛えてシェイプアップ。',
      ],
      photo: lpImage('modal-photo-surf-fit.webp'),
      logo: { src: lpImage('brand-surf-fit.webp'), background: '#FFFFFF' },
    },
  },
  {
    src: lpImage('brand-insea.webp'),
    alt: 'INSEA',
    modal: {
      title: 'Insea',
      subtitle: [
        'ヨガ・ピラティス・ダンス・ストレッチ・有酸素など豊富なレッスンをホット環境で体験',
      ],
      body: [
        '＜マグマスタジオとは＞',
        '体温と免疫力向上のエビデンスがある温活スタジオ。それが「マグマスパスタジオ」です。体温が１℃上がれば免疫力は２～６倍になると言われています。自分の身体を自分の力（免疫力）で守る事が出来れば最も効果的な抗ウイルス対策です。一般的なホットヨガはヒーターや加湿器で温湿度を高めているのに比べinseaは溶岩プレートから出る水蒸気で室内を温めるため息苦しさもなく、遠赤外線効果で深部体温を高めながらリラックスした状態で発汗できます。',
      ],
      photo: lpImage('modal-photo-insea.webp'),
      logo: { src: lpImage('brand-insea.webp'), background: '#FFFFFF' },
    },
  },
  {
    src: lpImage('brand-loive.webp'),
    alt: 'loIve',
    modal: {
      title: 'LoIve',
      subtitle: [
        '「わたしを好きになる」をテーマにした',
        '女性専用ホットヨガスタジオスタジオ',
      ],
      body: [
        '「ロイブ（loIve）」はホットヨガを通じて、ありのままの自分に向き合い、変化を感じ、ロイブとの出会いで“自分を、人生を愛するきっかけ”になれたらと願っています。',
        '定番のヨガはもちろん、暗闇エクササイズ「Beat Drum Diet」や相撲の動きを取り入れた「SUMO YOGA」など、楽しくカラダを動かせるプログラムが充実。',
        'また女性だけのスタジオなので、気兼ねなくノーメイクで、たくさん汗をかいたりポーズを取ったりでき、集中してホットヨガの時間をお過ごしいただけます。',
      ],
      photo: lpImage('modal-photo-loive.webp'),
      logo: { src: lpImage('brand-loive.webp'), background: '#FFFFFF' },
    },
  },
  {
    src: lpImage('brand-zerogym.webp'),
    alt: 'ZEROGYM',
    modal: {
      title: 'ZERO GYM',
      subtitle: ['静寂の空間であなたの疲れた頭と体をリセット'],
      body: [
        'ラウンジのような閑静なスタジオの中で、独自のメソッドのストレッチや瞑想を行い、肩こりや腰痛など体の不調を改善し、整った心身で仕事のパフォーマンスを最大化させます。',
        'また、基本的に男女別のプログラムとなっており、異性の目を気にせずにトレーニングを行えます。',
      ],
      photo: lpImage('modal-photo-zerogym.webp'),
      logo: { src: lpImage('brand-zerogym.webp'), background: '#FFFFFF' },
    },
  },
]

/** お客様の声セクション上の写真ストリップ */
export const PHOTO_STRIP: string[] = [
  lpImage('activity-yoga.webp'),
  lpImage('strip-treadmill.webp'),
  lpImage('strip-machine-gym.webp'),
  lpImage('strip-pool.webp'),
  lpImage('strip-trainer.webp'),
  lpImage('activity-bouldering.webp'),
]

export interface Voice {
  /** 企業名 (配列の要素ごとに改行) */
  company: string[]
  logo: LpImage
  employees: number
  /** 業種 (配列の要素ごとに改行) */
  industry: string[]
  catchphrase: string
  body: string
}

/** ご導入いただいた法人会員様の声 (カルーセルの初期表示は先頭) */
export const VOICES: Voice[] = [
  {
    company: ['株式会社Dot homes様'],
    logo: { src: lpImage('voice-dot-homes.webp'), alt: 'Dot HOMES' },
    employees: 30,
    industry: ['コンサルティング'],
    catchphrase:
      '全国どこでも利用できる柔軟性が決め手。男女共に利用しやすいことから利用者が拡大。社内のコミュニケーション機会にも貢献。',
    body: 'リモートワーク環境での生産性向上を目的に導入しました。多様なプログラムが利用できるため、男女ともに使いやすいことが好評です。従業員同士の共同利用にもつながっており、健康促進だけでなく社内コミュニケーションの活性化にも効果を発揮しています。',
  },
  {
    company: ['株式会社renue様'],
    logo: { src: lpImage('voice-renue.webp'), alt: 'renue' },
    employees: 20,
    industry: ['コンサルティング'],
    catchphrase:
      '業務パフォーマンス向上を目的に導入。運動経験者が初心者を誘う文化が醸成され、 運動習慣のなかった従業員の利用拡大とチームビルディングに貢献。',
    body: '社長が積極的にみんなで利用していくことを呼びかけたことで、トレーニング経験者を中心に利用が広がりました。登録が簡単で気軽に利用できることもあり、今では経験者が初心者を誘う文化が醸成され、運動習慣のなかった従業員も利用を始めています。こうした取り組みが、社内交流の促進やチームビルディングにも貢献しています。',
  },
  {
    company: ['株式会社Rezony'],
    logo: { src: lpImage('voice-rezony.webp'), alt: 'Rezony' },
    employees: 10,
    industry: ['パーソナル', 'トレーニングジム運営'],
    catchphrase:
      '気になっていたジムや過去に通っていたヨガスタジオなど、様々な施設が定期的に増えることで継続利用へつながりやすい環境を実現。',
    body: '従業員の健康が仕事のパフォーマンス向上につながると考え、誰もが使いやすい健康増進サービスを探していました。店舗や居住地、興味が異なる従業員に対応できる豊富な施設数とプログラム、広い対応エリアが決め手となり導入を決めました。「気になっていたジムが使える」「新しい施設が増えて飽きない」など好評の声が多く、定期的に運動する習慣が定着してきています。',
  },
  {
    company: ['監査法人アヴァンティア'],
    logo: { src: lpImage('voice-avantia.webp'), alt: '監査法人アヴァンティア' },
    employees: 210,
    industry: ['監査法人'],
    catchphrase:
      '従業員の健康支援のため4拠点に導入し、公平な利用機会を提供。 柔軟な働き方に対応した福利厚生として求職者へ訴求し、差別化を実現。',
    body: '従業員の健康促進を目的に導入し、東京・大阪・福岡・札幌の4拠点で公平な利用機会の提供を実現しています。採用イベントでは、柔軟な働き方に合わせた福利厚生としてGYYMを紹介し、他社との差別化につながっています。GYYMの導入は経営層が真剣に従業員の健康面のケアに取り組んでいることが伝わり、社内外から評価を得ています。',
  },
  {
    company: ['医療法人正育会', '春木レディースクリニック様'],
    logo: { src: lpImage('voice-haruki.webp'), alt: 'HARUKI LADIES CLINIC' },
    employees: 90,
    industry: ['医療'],
    catchphrase:
      '様々な場所から利用できることから導入。全従業員90名が利用アカウントを登録し、運動を始める従業員や継続利用が実現。',
    body: '様々な場所から利用できるため、全従業員が使いやすいと考え導入しました。 法人負担で気軽に施設を利用できることが好評で、新たに運動を始める従業員や、 既にジムに通う従業員の運動体験を充実させることに貢献しています。',
  },
]

export interface PlanFeature {
  text: string
  /** 注記 (灰色の小さい文字) */
  note?: string
}

export const PLAN_FEATURES: PlanFeature[] = [
  { text: '登録人数に応じた料金設定で、無駄なくコスト管理が可能' },
  {
    text: 'プランに合わせて予算を管理。超過分は従業員様が直接負担するため、経費精算の手続きが不要',
  },
  {
    text: 'ポイント追加購入時の決済手数料が無料*',
    note: '＊12,000pt未満のポイント購入には400円（税込）の決済手数料がかかりますが、従業員様は当該手数料が無料となります。',
  },
  { text: '従業員様のGYYMご利用実績・状況を管理画面でご確認可能' },
]

export const PLAN_PHOTOS: string[] = [
  lpImage('plan-photo-1.webp'),
  lpImage('plan-photo-2.webp'),
]

export interface Faq {
  number: string
  question: string
  /** 回答 (配列の要素ごとに改行) */
  answer: string[]
  link?: { label: string; href: string }
}

export const FAQS: Faq[] = [
  {
    number: '01',
    question: 'オフィス近くのジムを調べてみたい',
    answer: [
      '下記のボタンより検索いただけます。現在地からお近くのジムを調べるだけでなく、エリアや施設名、アクティビティの種類などでも調べることができるので、ぜひご覧ください。',
    ],
    link: { label: '検索はこちら', href: LINKS.searchByDistance },
  },
  {
    number: '02',
    question: '法人負担額を設定しない場合、従業員自身で決済するの？',
    answer: [
      '不足するポイントは、従業員様ご自身のクレジットカードで決済いただきます。',
    ],
  },
  {
    number: '03',
    question: '法人負担額のオプション利用に別途料金はかかるの？',
    answer: [
      '法人負担額のオプション利用に別途料金はかかりません。',
      '従業員様に付与させて頂くポイント（法人会員ポイント）の内、従業員様が使用された分のみ、法人ご担当者様へご請求させていただきます。使用されなかった法人会員ポイントは90日で失効となり、ご請求対象とはなりません。',
    ],
  },
  {
    number: '04',
    question: '法人会員サービス開始までの流れを確認したい',
    answer: [
      'ご利用開始までに、サービスのご案内やご契約詳細の確認のため、一度オンラインでお打合せをさせて頂いております。お手数ですが、下記資料請求フォームよりご連絡をお待ちしております。',
      '法人会員サービスは、お打合せ及びご契約手続きが完了した月の翌月1日より開始できます。ご契約手続き、ご利用環境の設定に2週間程度いただいておりますので、ご了承をお願いします。',
    ],
  },
  {
    number: '05',
    question: '健康経営優良法人の認定に利用できる？',
    answer: [
      '健康経営優良法人認定の項目の一つとして、「健康増進・生活習慣病予防対策」がございます。本サービスをご導入いただくことで、「運動機会の増進に向けた取り組み」の評価項目に寄与できるものと考えてございます。',
    ],
  },
]

/** 資料請求フォームの従業員数選択肢 */
export const EMPLOYEE_COUNT_OPTIONS = [
  '〜10人未満',
  '10〜50人未満',
  '50〜100人未満',
  '100〜500人未満',
  '500人以上',
] as const

export type EmployeeCountOption = (typeof EMPLOYEE_COUNT_OPTIONS)[number]

export interface FooterLink {
  label: string
  href: string
  external?: boolean
}

export const FOOTER_LINKS: FooterLink[] = [
  { label: '会社概要', href: LINKS.company },
  { label: '特定商取引法に基づく表記', href: LINKS.law },
  { label: 'プライバシーポリシー', href: LINKS.policy, external: true },
  { label: '利用規約', href: LINKS.term, external: true },
  { label: 'FAQ・お問い合わせ', href: LINKS.support, external: true },
  {
    label: '提携に関するお問い合わせ',
    href: LINKS.partnerContact,
    external: true,
  },
  { label: '法人会員サービスに関するお問い合わせ', href: LINKS.corpContact },
]
